import type { OpenAccountType } from './types';
import {
  useOpenAccountSpot,
  useOpenAccountInvest,
  useOpenAccountMargin,
  useOpenAccountEarn,
} from '../api/queries';
import { useMemo } from 'react';
import type { OpenAccountResponseDto } from '../api/types';

const useOpenAccountRequest = ({
  accountType,
  onSuccess,
  assetCode,
}: {
  accountType: OpenAccountType;
  onSuccess: (
    data: OpenAccountResponseDto,
    accountType: OpenAccountType,
    assetCode?: string
  ) => void;
  assetCode?: string;
}) => {
  const openAccountOptions = useMemo(() => {
    return {
      onSuccess: (data: OpenAccountResponseDto) => {
        onSuccess(data, accountType, assetCode);
      },
    };
  }, [onSuccess, accountType, assetCode]);

  const openAccountSpotMutation = useOpenAccountSpot(openAccountOptions);
  const openAccountInvestMutation = useOpenAccountInvest(openAccountOptions);
  const openAccountMarginMutation = useOpenAccountMargin(openAccountOptions);
  const openAccountEarnMutation = useOpenAccountEarn(openAccountOptions);

  const {
    openAccount,
    isOpenAccountPending,
    isOpenAccountSuccess,
    isOpenAccountError,
    openAccountData,
    openAccountError,
  } = useMemo(() => {
    switch (accountType) {
      case 'spot':
        return {
          ...openAccountSpotMutation,
        };
      case 'invest':
        return {
          ...openAccountInvestMutation,
        };
      case 'margin':
        return {
          ...openAccountMarginMutation,
        };
      case 'earn':
        return {
          ...openAccountEarnMutation,
        };
      default:
        return {
          openAccount: () => {
            throw new Error('Invalid account type');
          },
          isOpenAccountPending: false,
          isOpenAccountSuccess: false,
          isOpenAccountError: false,
          openAccountData: undefined,
          openAccountError: undefined,
        };
    }
  }, [
    accountType,
    openAccountSpotMutation,
    openAccountInvestMutation,
    openAccountMarginMutation,
    openAccountEarnMutation,
  ]);

  return {
    openAccount,
    isOpenAccountPending,
    isOpenAccountSuccess,
    isOpenAccountError,
    openAccountData,
    openAccountError,
  };
};

export { useOpenAccountRequest };
